import { RequestDetailResponse } from '@/api/apps/insider';
import { useTranslate } from '@/hooks/useTranslate';
import { Box, Divider, Typography } from '@mui/material';

interface Props {
    data: RequestDetailResponse['adviser'] | undefined
}
const Advise = ({ data }: Props) => {
    const { t } = useTranslate('insider');

    if (!data) {
        return null;
    }

    const recommendations = data.recommendations || [];
    const potential_markets = data.potential_markets || [];
    const marketing_ideas = data.marketing_ideas || [];
    const potential_consumers = data.potential_consumers || [];
    const risks = data.risks || [];
    const pros_and_cons = {
        pros: data.pros_and_cons?.pros || [],
        cons: data.pros_and_cons?.cons || [],
    }

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
            }}>

                {/* Recommendations */}

                {recommendations.length > 0 && (
                    <>
                        <Box data-row>
                            <Typography variant="h4" gutterBottom>
                                {t('adviser.recommendations')}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 5,
                            }}>
                                {recommendations.map((rec, index) => (
                                    <Box key={index}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                        }}>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {rec.label}
                                            </Typography>
                                            —
                                            <Typography variant="subtitle2" color={rec.priority === "high" ? "error" : "warning"}>
                                                {rec.priority}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2">
                                            {rec.description}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Divider />
                    </>
                )}


                {/* Potential Markets */}
                {potential_markets.length > 0 && (
                    <>
                        <Box data-row>
                            <Typography variant="h4" gutterBottom>
                                {t('adviser.potential-markets')}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {potential_markets.map((market) => market.name).join(", ")}
                            </Typography>
                        </Box>

                        <Divider />
                    </>
                )}

                {/* Marketing Ideas */}
                {marketing_ideas.length > 0 && (
                    <>
                        <Box data-row>
                            <Typography variant="h4" gutterBottom>
                                {t('adviser.marketing-ideas')}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 5,
                            }}>
                                {marketing_ideas.map((idea, index) => (
                                    <Box key={index}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                            {idea.channel}
                                        </Typography>
                                        <Typography variant="body2">
                                            {idea.description}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Divider />
                    </>
                )}

                {/* Potential Consumers */}
                {potential_consumers.length > 0 && (
                    <>
                        <Box data-row>
                            <Typography variant="h4" gutterBottom>
                                {t('adviser.potential-consumers')}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 5,
                            }}>
                                {potential_consumers.map((consumer, index) => (
                                    <Box key={index}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                            {consumer.description}
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>{t('adviser.jobs')}:</b> {consumer.jobs}
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>{t('adviser.pain-points')}:</b> {consumer.pain_points}
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>{t('adviser.gains')}:</b> {consumer.gains}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Divider />
                    </>
                )}

                {/* Risks */}
                {risks.length > 0 && (
                    <>
                        <Box data-row>
                            <Typography variant="h4" gutterBottom>
                                {t('adviser.risks')}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 5,
                            }}>
                                {risks.map((risk, index) => (
                                    <Box key={index}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="error">
                                            {risk.type}
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>{t('adviser.description')}:</b> {risk.description}
                                        </Typography>
                                        <Typography variant="body2">
                                            <b>{t('adviser.mitigation')}:</b> {risk.mitigation}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Divider />
                    </>
                )}

                {/* Pros and Cons */}
                <Box data-row>
                    <Typography variant="h4" gutterBottom>
                        {t('adviser.pros-and-cons')}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                    }}>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" color="success">
                                {t('adviser.pros')}
                            </Typography>
                            <Typography variant="body2">
                                {pros_and_cons.pros.join(", ")}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" color="error">
                                {t('adviser.cons')}
                            </Typography>
                            <Typography variant="body2">
                                {pros_and_cons.cons.join(", ")}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default Advise;
